import { EnvironmentProviders } from '@angular/core';
import { InboxState } from '@app/_shared/widgets/messages/_store/inbox/inbox.reducer';
import { inboxStore } from '@app/_shared/widgets/messages/_store/inbox/inbox.store';
// Root reducers
import { AppGlobalEffects } from '@app/_store/effects/global.effects';
import {
  AccountsState,
  accountsStore,
  AppActionsState,
  appActionsStore,
  AppSettingsState,
  appSettingsStore,
  BudgetsState,
  budgetsStore,
  CategoriesState,
  categoriesStore,
  DebtPayoffState,
  debtPayoffStore,
  GoalsState,
  goalsStore,
  IconsState,
  iconsStore,
  LeftoverState,
  leftoverStore,
  OnboardingState,
  onboardingStore,
  ProductsInfoState,
  productsInfoStore,
  ProfileState,
  profileStore,
  PropertyState,
  propertyStore,
  QuizFunnelState,
  quizFunnelStore,
  RegularPaymentsState,
  regularPaymentsStore,
  SupportState,
  supportStore,
  TransactionsState,
  transactionsStore,
  UserInfoState,
  userInfoStore,
  UserPlanState,
  userPlanStore,
  UserSettingsState,
  userSettingsStore,
} from '@app/_store/features';
import { BannersState } from '@app/_store/features/banners/banners.reducer';
import { bannersStore } from '@app/_store/features/banners/banners.store';
import { BillsharkInfoState } from '@app/_store/features/billshark-info/billshark-info.reducer';
import { billsharkInfoStore } from '@app/_store/features/billshark-info/billshark-info.store';
import { BillsharkProvidersState } from '@app/_store/features/billshark-providers/billshark-providers.reducer';
import { billsharkProvidersStore } from '@app/_store/features/billshark-providers/billshark-providers.store';
import { insightsReportStore } from '@app/_store/features/insights-report/insights-report.store';
import { PaceState } from '@app/_store/features/pace/pace.reducer';
import { paceStore } from '@app/_store/features/pace/pace.store';
import { PocketState } from '@app/_store/features/pocket/pocket.reducer';
import { pocketStore } from '@app/_store/features/pocket/pocket.store';
import { rulesStore } from '@app/_store/features/rules/rules.store';
import { StaticOptionsState } from '@app/_store/features/static-options/static-options.reducer';
import { staticOptionsStore } from '@app/_store/features/static-options/static-options.store';
import { SubscriptionsCheckupState } from '@app/_store/features/subscriptions-checkup/subscriptions-checkup.reducer';
import { subscriptionsCheckupStore } from '@app/_store/features/subscriptions-checkup/subscriptions-checkup.store';
// A9c. Colocated feature stores: these live inside their feature now; the
// composition root imports them from there, concrete files, no barrel.
import { TopInstitutionsState } from '@app/_store/features/top-institutions/top-institutions.reducer';
import { topInstitutionsStore } from '@app/_store/features/top-institutions/top-institutions.store';
import { UserInstitutionLoginsState } from '@app/_store/features/user-institution-logins/user-institution-logins.reducer';
import { userInstitutionLoginsStore } from '@app/_store/features/user-institution-logins/user-institution-logins.store';
import { metaReducers, ROOT_REDUCERS } from '@app/_store/root-reducers';
import { Rules } from '@core/models/interfaces/storage-data/rules';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, RouterState } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';

/** Substituted by the builder's `define`; see angular.json. */
declare const PG_STORE_DEVTOOLS: boolean;

export interface GlobalStateValues {
  appActions: AppActionsState;
  appSettings: AppSettingsState;
  profile: ProfileState;
  quizFunnel: QuizFunnelState;
  userInfo: UserInfoState;
  userPlanState: UserPlanState;
  supportSettings: SupportState;
  inbox: InboxState;
  inAppOnboarding: OnboardingState;
  inMyPocket: PocketState;
  regularPayments: RegularPaymentsState;
  // The container, not `RulesState`: `sync/fetchData` only ever delivers
  // `collection` and `metaData`, and the rest of that slice is local save state.
  rules: Rules;
  budgets: BudgetsState;
  accounts: AccountsState;
  transactions: TransactionsState;
  categories: CategoriesState;
  debtPayoff: DebtPayoffState;
  property: PropertyState;
  goals: GoalsState;
  banners: BannersState;
  icons: IconsState;
  productsInfo: ProductsInfoState;
  userInstitutionLogins: UserInstitutionLoginsState;
  topInstitutions: TopInstitutionsState;
  userSettings: UserSettingsState;
  staticOptions: StaticOptionsState;
  leftover: LeftoverState;
  pace: PaceState;
  billsharkInfo: BillsharkInfoState;
  billsharkProviders: BillsharkProvidersState;
  subscriptionsCheckup: SubscriptionsCheckupState;
}

export const rootStoreProviders: EnvironmentProviders[] = [
  // `ROOT_REDUCERS` is the store's root reducer map, so its keys (`router`,
  // `appDataLoading`) live at the top level. Registering the same map again as a
  // feature named 'root' duplicated both of them: two copies of every router
  // snapshot in memory, and both reducers running on every action.
  provideStore(ROOT_REDUCERS, { metaReducers }),
  provideRouterStore({ routerState: RouterState.Minimal }),
  // Developer tooling only. `logOnly: isDevMode()` used to read backwards: it handed
  // production the full feature set (dispatch, time travel) over the whole store and
  // restricted the developer instead. The flag comes from the builder's `define`
  // (angular.json) rather than from `environment`, because only a literal substituted
  // at build time lets the bundler drop this branch — a property read off the
  // environment object stays in the bundle even when it can never be true.
  // The `typeof` guard keeps targets that pass no `define` (e.g. `ng test`) working.
  ...(typeof PG_STORE_DEVTOOLS !== 'undefined' && PG_STORE_DEVTOOLS
    ? [
        provideStoreDevtools({
          name: 'PG WEB Store',
          maxAge: 25,
        }),
      ]
    : []),
  provideEffects(AppGlobalEffects),
  ...appActionsStore,
  ...appSettingsStore,
  ...userInfoStore,
  ...userPlanStore,
  // budgets and goals register before regular-payments and debt-payoff on
  // purpose: those two effect classes construct UserService, whose field
  // initializers select budgets/goals/userPlan synchronously — with the old
  // order the states did not exist yet, and every app start logged the
  // dev-mode "feature name does not exist in the state" warning.
  ...budgetsStore,
  ...goalsStore,
  ...userInstitutionLoginsStore,
  ...userSettingsStore,
  ...profileStore,
  ...quizFunnelStore,
  ...supportStore,
  ...inboxStore,
  ...onboardingStore,
  ...pocketStore,
  ...regularPaymentsStore,
  ...rulesStore,
  ...accountsStore,
  ...transactionsStore,
  ...categoriesStore,
  ...debtPayoffStore,
  ...propertyStore,
  ...bannersStore,
  ...billsharkInfoStore,
  ...billsharkProvidersStore,
  ...subscriptionsCheckupStore,
  ...iconsStore,
  ...productsInfoStore,
  ...topInstitutionsStore,
  ...staticOptionsStore,
  ...leftoverStore,
  ...paceStore,
  ...insightsReportStore,
];
